// @ts-nocheck
import { localDateToIsoYmd, parseWorkflowDate } from '../../../utils/isoDateDisplay';

const DEFAULT_VESTING_DETAILS = { months: 48, cliffMonths: 12 };

const VESTING_SCHEDULES = {
  'monthly-4yr-1cliff': { months: 48, cliffMonths: 12 },
  'quarterly-4yr-1cliff': { months: 48, cliffMonths: 12 },
  'annual-4yr': { months: 48, cliffMonths: 0 },
  'annual-4yr-1cliff': { months: 48, cliffMonths: 12 },
  'monthly-3yr-1cliff': { months: 36, cliffMonths: 12 },
  'quarterly-3yr-1cliff': { months: 36, cliffMonths: 12 },
  'annual-3yr': { months: 36, cliffMonths: 0 },
  'monthly-4yr': { months: 48, cliffMonths: 0 },
  'quarterly-4yr': { months: 48, cliffMonths: 0 },
  '2yr-cliff': { months: 24, cliffMonths: 24 },
};

const monthsBetween = (d1, d2) => {
  const start = parseWorkflowDate(d1);
  const end = parseWorkflowDate(d2);
  if (!start || !end) return 0;
  return Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24 * 30.44));
};

const fullCalendarMonthsBetween = (d1, d2) => {
  const start = parseWorkflowDate(d1);
  const end = parseWorkflowDate(d2);
  if (!start || !end || end <= start) return 0;
  let months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
  if (end.getDate() < start.getDate()) months--;
  return Math.max(0, months);
};

export function getVestingDetailsForAward(award) {
  if (award?.vestingSchedule === 'custom') {
    return {
      months: +award.customVestingMonths || 0,
      cliffMonths: +award.customCliffMonths || 0,
    };
  }
  return VESTING_SCHEDULES[award?.vestingSchedule] || DEFAULT_VESTING_DETAILS;
}

export function isGrantWithin12Months(grantDate, closingDate) {
  if (!grantDate || !closingDate) return false;
  const grant = parseWorkflowDate(grantDate);
  const closing = parseWorkflowDate(closingDate);
  if (!grant || !closing) return false;
  const monthsDiff = (closing.getFullYear() - grant.getFullYear()) * 12 + (closing.getMonth() - grant.getMonth());
  return monthsDiff < 12;
}

/**
 * Full Q&A-24(c) breakdown for methodology UI and diagnostics.
 * `uses24cDiscount` is true when the lapse + PV acceleration formula is applied (not full spread).
 */
export function calculateEquityAwardQa24cBreakdown({ award, closingDate, dealPrice, discountRateForPV }) {
  const totalShares = +award?.totalShares || 0;
  const accelerationType = award?.accelerationType || 'full';
  const accelerationPct =
    accelerationType === 'partial'
      ? (+award?.accelerationPercentage || 100) / 100
      : accelerationType === 'full'
        ? 1
        : 0;
  const treatmentOnCIC = award?.treatmentOnCIC || 'cashedOut';
  const vestingType = award?.vestingType || 'time';
  const performanceConditionMet = award?.performanceConditionMet === 'yes';
  const grantWithin12Months = isGrantWithin12Months(award?.grantDate, closingDate);

  const emptyBase = {
    earlyExit: 'none',
    totalShares,
    accelerationType,
    treatmentOnCIC,
    vestingType,
    performanceConditionMet,
    grantWithin12Months,
    monthsElapsed: 0,
    vestingDetails: getVestingDetailsForAward(award),
    sharesVestedPrior: 0,
    unvestedBeforeAccel: 0,
    sharesVestingUponCIC: 0,
    fullyVested: true,
    perShareValue: 0,
    fullValue: 0,
    qualifies24c: false,
    useFullValue: !!award?.useFullValue,
    uses24cDiscount: false,
    vestingEndDateYmd: null,
    monthsAccelerated: 0,
    yearsAccelerated: 0,
    periods: 0,
    semiannualRate: 0,
    pvFactor: 1,
    lapseComponent: 0,
    accelerationComponent: 0,
    parachuteValue: 0,
    discountRateForPV,
    reductionFromFull: 0,
    reductionPct: 0,
  };

  if (accelerationType === 'none' || (award?.type === 'option' && treatmentOnCIC === 'rolledOver')) {
    return {
      ...emptyBase,
      earlyExit: 'no_accel',
      sharesVestedPrior: totalShares,
      fullyVested: true,
      qualifies24c: false,
    };
  }

  if (!award?.grantDate || !closingDate || totalShares === 0) {
    return {
      ...emptyBase,
      earlyExit: 'missing_inputs',
      qualifies24c: false,
    };
  }

  const vestingDetails = emptyBase.vestingDetails;
  const monthsElapsed = monthsBetween(award.grantDate, closingDate);
  let sharesVestedPrior = 0;
  let unvested = totalShares;
  if (monthsElapsed >= vestingDetails.months) {
    sharesVestedPrior = totalShares;
    unvested = 0;
  } else if (monthsElapsed >= vestingDetails.cliffMonths) {
    sharesVestedPrior = Math.floor(totalShares * (monthsElapsed / vestingDetails.months));
    unvested = totalShares - sharesVestedPrior;
  }

  const sharesVestingUponCIC = Math.floor(unvested * accelerationPct);
  const fullyVested = sharesVestingUponCIC === 0;

  let perShareValue = 0;
  if (award.type === 'option') {
    perShareValue = Math.max(0, dealPrice - (+award.exercisePrice || 0));
  } else if (award.type === 'restricted_stock') {
    perShareValue = dealPrice - (+award.purchasePrice || 0);
  } else {
    perShareValue = dealPrice;
  }

  const fullValue = sharesVestingUponCIC * perShareValue;

  const qualifies24c =
    fullValue > 0 && !grantWithin12Months && (vestingType !== 'performance' || performanceConditionMet);

  const grantDateParsed = parseWorkflowDate(award.grantDate);
  let vestingEndDateYmd = null;
  let monthsAccelerated = 0;
  let yearsAccelerated = 0;
  let periods = 0;
  let semiannualRate = 0;
  let pvFactor = 1;
  let lapseComponent = 0;
  let accelerationComponent = 0;

  const apply24c = qualifies24c && !award.useFullValue && !!grantDateParsed && fullValue > 0;

  if (grantDateParsed) {
    const vestingEndDate = new Date(grantDateParsed);
    vestingEndDate.setMonth(vestingEndDate.getMonth() + vestingDetails.months);
    vestingEndDateYmd = localDateToIsoYmd(vestingEndDate);
    monthsAccelerated = fullCalendarMonthsBetween(closingDate, vestingEndDate);
  }

  if (apply24c) {
    yearsAccelerated = monthsAccelerated / 12;
    periods = yearsAccelerated * 2;
    semiannualRate = discountRateForPV / 2;
    pvFactor = Math.pow(1 + semiannualRate, periods);
    accelerationComponent = fullValue - fullValue / pvFactor;
    lapseComponent = fullValue * 0.01 * monthsAccelerated;
  }

  const parachuteValue = apply24c ? Math.min(fullValue, accelerationComponent + lapseComponent) : fullValue;

  const reductionFromFull = fullValue - parachuteValue;
  const reductionPct = fullValue > 0 ? reductionFromFull / fullValue : 0;

  return {
    earlyExit: 'none',
    totalShares,
    accelerationType,
    treatmentOnCIC,
    vestingType,
    performanceConditionMet,
    grantWithin12Months,
    monthsElapsed,
    vestingDetails,
    sharesVestedPrior,
    unvestedBeforeAccel: unvested,
    sharesVestingUponCIC,
    fullyVested,
    perShareValue,
    fullValue,
    qualifies24c,
    useFullValue: !!award.useFullValue,
    uses24cDiscount: apply24c,
    vestingEndDateYmd,
    monthsAccelerated,
    yearsAccelerated,
    periods,
    semiannualRate,
    pvFactor,
    lapseComponent,
    accelerationComponent,
    parachuteValue,
    discountRateForPV,
    reductionFromFull,
    reductionPct,
  };
}

export function calculateEquityAwardQa24cValue({ award, closingDate, dealPrice, discountRateForPV }) {
  const b = calculateEquityAwardQa24cBreakdown({ award, closingDate, dealPrice, discountRateForPV });
  return {
    sharesVestedPrior: b.sharesVestedPrior,
    sharesVestingUponCIC: b.sharesVestingUponCIC,
    fullyVested: b.fullyVested,
    fullValue: b.fullValue,
    parachuteValue: b.parachuteValue,
    qualifies24c: b.qualifies24c,
  };
}
